/**
 * Registration Form Component
 * Handles new user signup with email/password and 14-day free trial
 */

import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import { AlertCircle, CheckCircle2, Mail, Lock, User } from 'lucide-react';
import { trackFunnel } from '../../services/analytics';

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  terms?: string;
}

export default function RegistrationForm() {
  const navigate = useNavigate();
  // Get email from URL if present
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const emailFromUrl = query.get('email');

  const { signUp, signInWithGoogle, initializeUserAccount, user, isAuthorized } = useAuth();

  const [formData, setFormData] = useState({
    email: emailFromUrl || user?.email || '',
    password: '',
    confirmPassword: '',
    firstName: user?.displayName?.split(' ')[0] || '',
    lastName: user?.displayName?.split(' ')[1] || '',
    acceptedTerms: false,
    selectedPlan: 'free' as 'free' | 'commercial' | 'enterprise'
  });

  // Update email if user logs in while on this page
  React.useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email!,
        firstName: user.displayName?.split(' ')[0] || prev.firstName,
        lastName: user.displayName?.split(' ')[1] || prev.lastName
      }));
    }
  }, [user]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const isAuthorizedRef = React.useRef(isAuthorized);

  React.useEffect(() => {
    isAuthorizedRef.current = isAuthorized;
  }, [isAuthorized]);

  const isPreAuthenticated = !!user;

  const waitForAuthorization = async (timeoutMs: number = 6000): Promise<void> => {
    const start = Date.now();
    while (!isAuthorizedRef.current && Date.now() - start < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  };

  const redirectAfterRegistration = async () => {
    await waitForAuthorization();

    if (formData.selectedPlan === 'commercial') {
      navigate('/settings?plan=commercial&checkout=true');
    } else if (formData.selectedPlan === 'enterprise') {
      navigate('/settings?plan=enterprise&checkout=true');
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoogleSignUp = async () => {
    setServerError(null);
    setLoading(true);

    try {
      trackFunnel('user_onboarding', 'submit_google_registration', 2, { method: 'google' });
      await signInWithGoogle();
      await redirectAfterRegistration();
    } catch (error: any) {
      console.error('Google sign-up error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setServerError('Sign-up cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        setServerError('Please allow popups for this site');
      } else {
        setServerError(error.message || 'Google sign-up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!isPreAuthenticated) {
      // Password validation
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, and number';
      }

      // Confirm password
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Terms acceptance
    if (!formData.acceptedTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      trackFunnel('user_onboarding', 'submit_email_registration', 2, { method: 'email', plan: formData.selectedPlan });
      if (!isPreAuthenticated) {
        await signUp(formData.email, formData.password);
      } else if (user) {
        // If already authenticated (e.g. via Google) but not authorized (not in DB),
        // we need to initialize their account now.
        await initializeUserAccount(user);
      }

      await redirectAfterRegistration();
    } catch (error: any) {
      console.error('Registration error:', error);

      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        setServerError('An account with this email already exists');
      } else if (error.code === 'auth/weak-password') {
        setServerError('Password is too weak. Please use a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        setServerError('Invalid email address');
      } else {
        setServerError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Plan Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Choose Your Plan
        </label>
        <div className="grid grid-cols-3 gap-3">
          {/* Free Plan */}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedPlan: 'free' })}
            className={`p-4 border-2 rounded-lg text-left transition-all ${formData.selectedPlan === 'free'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-1">
              <div className="font-semibold text-gray-900">Free</div>
              <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Forever</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">$0/mo</div>
            <div className="text-xs text-gray-500 mt-1">Up to 10 properties</div>
          </button>

          {/* Commercial Plan */}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedPlan: 'commercial' })}
            className={`p-4 border-2 rounded-lg text-left transition-all ${formData.selectedPlan === 'commercial'
              ? 'border-[#2563EB] bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-1">
              <div className="font-semibold text-gray-900">Commercial</div>
            </div>
            <div className="text-sm text-gray-600 mt-1">$49/mo</div>
            <div className="text-xs text-gray-500 mt-1">Unlimited properties</div>
          </button>

          {/* Enterprise Plan */}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedPlan: 'enterprise' })}
            className={`p-4 border-2 rounded-lg text-left transition-all ${formData.selectedPlan === 'enterprise'
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="font-semibold text-gray-900">Enterprise</div>
            <div className="text-sm text-gray-600 mt-1">$199/mo</div>
            <div className="text-xs text-gray-500 mt-1">Custom limits & support</div>
          </button>
        </div>
      </div>

      {/* Free Plan Banner */}
      {formData.selectedPlan === 'free' && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-900 mb-2">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">Free Forever</span>
          </div>
          <p className="text-sm text-green-700">
            Full access to all features for up to 10 properties. No credit card required.
          </p>
        </div>
      )}

      {/* Paid Plan Info - Show if commercial or enterprise selected */}
      {formData.selectedPlan !== 'free' && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-900 mb-2">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">
              {formData.selectedPlan === 'commercial' ? 'Commercial Plan' : 'Enterprise Plan'}
            </span>
          </div>
          <p className="text-sm text-blue-700">
            You'll be redirected to payment after registration.
          </p>
        </div>
      )}

      {/* Server Error Alert */}
      {serverError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{serverError}</p>
          </div>
        </div>
      )}

      {/* Google Sign-Up Button - Only show if not already authenticated */}
      {!isPreAuthenticated && (
        <>
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign up with Google
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="you@example.com"
              disabled={loading || isPreAuthenticated}
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John"
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Doe"
              disabled={loading}
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password}</p>
          )}
          {!errors.password && formData.password && (
            <p className="mt-1 text-xs text-gray-500">
              Minimum 8 characters with uppercase, lowercase, and number
            </p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
          )}
        </div>

        {/* Terms Checkbox */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.acceptedTerms}
              onChange={(e) => handleInputChange('acceptedTerms', e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-700 underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                Privacy Policy
              </Link>
            </span>
          </label>
          {errors.terms && (
            <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating Account...
            </>
          ) : (
            'Create Free Account'
          )}
        </button>

        {/* Login Link */}
        <div className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign In
          </Link>
        </div>
      </form>

      {/* Pricing Info */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600 text-center">
          Free tier includes up to 10 properties. No credit card required.
        </p>
        <p className="text-xs text-gray-500 text-center mt-1">
          Upgrade to Commercial for unlimited properties at $49/month anytime.
        </p>
      </div>
    </div>
  );
}
